/**
 * PipelineSelect — Sidebar list of pipelines for the current project.
 *
 * Shows pipeline name, source type badge, target type badge, and status chip.
 * Hovering a row reveals Edit and Delete action buttons (hover-revealed pattern).
 * Empty state uses the shared <EmptyState> component (NFR30).
 */

import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { EmptyState } from "@/shared/components/platform/EmptyState";
import type { PEPipeline } from "../../features/pipeline-engineer/types";
import { listPipelines } from "../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// Connector type display labels
// ---------------------------------------------------------------------------

const CONNECTOR_LABELS: Record<string, string> = {
  mysql: "MySQL",
  postgres: "PostgreSQL",
  oracle: "Oracle",
  flatfile: "Flat File",
  redshift: "Redshift",
  snowflake: "Snowflake",
  databricks: "Databricks",
};

function connectorLabel(type: string): string {
  return CONNECTOR_LABELS[type] ?? type;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PipelineSelectProps {
  /** Current project ID — used to scope the pipeline list in future stories. */
  projectId: string;
  /** Key incremented by parent to trigger a re-fetch (e.g. after delete). */
  refreshKey?: number;
  /** ID of the currently selected pipeline (for highlight). */
  selectedId?: string;
  onSelect: (pipeline: PEPipeline) => void;
  onNew: () => void;
  onEdit: (pipeline: PEPipeline) => void;
  onDelete: (pipeline: PEPipeline) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PipelineSelect({
  projectId: _projectId,
  refreshKey = 0,
  selectedId,
  onSelect,
  onNew,
  onEdit,
  onDelete,
}: PipelineSelectProps) {
  const [pipelines, setPipelines] = useState<PEPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipelines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPipelines();
      setPipelines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pipelines");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPipelines();
  }, [fetchPipelines, refreshKey]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <PipelineListHeader onNew={onNew} />
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="animate-spin text-white/30" />
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error !== null) {
    return (
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <PipelineListHeader onNew={onNew} />
        <EmptyState
          variant="system-issue"
          heading="Failed to load pipelines"
          description={error}
          primaryAction={{
            label: "Retry",
            onClick: () => void fetchPipelines(),
          }}
        />
      </div>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────
  if (pipelines.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <PipelineListHeader onNew={onNew} />
        <EmptyState
          variant="first-visit"
          heading="No pipelines yet"
          description="Create your first pipeline to start loading data from source to target."
        />
      </div>
    );
  }

  // ── List ─────────────────────────────────────────────────────────────────
  return (
    <div className="glass-card rounded-2xl p-4 space-y-3">
      <PipelineListHeader onNew={onNew} />
      <ul
        className="space-y-1"
        role="listbox"
        aria-label="Pipeline list"
        aria-multiselectable="false"
      >
        {pipelines.map((pipeline) => (
          <PipelineRow
            key={pipeline.id}
            pipeline={pipeline}
            isSelected={pipeline.id === selectedId}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PipelineListHeader({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white/80">Pipelines</h2>
      <button
        type="button"
        onClick={onNew}
        aria-label="Create new pipeline"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors"
      >
        <Plus size={13} aria-hidden="true" />
        New Pipeline
      </button>
    </div>
  );
}

interface PipelineRowProps {
  pipeline: PEPipeline;
  isSelected: boolean;
  onSelect: (pipeline: PEPipeline) => void;
  onEdit: (pipeline: PEPipeline) => void;
  onDelete: (pipeline: PEPipeline) => void;
}

function PipelineRow({
  pipeline,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}: PipelineRowProps) {
  return (
    <li
      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
        isSelected
          ? "bg-brand-500/15 border border-brand-500/30"
          : "hover:bg-white/5 border border-transparent"
      }`}
      role="option"
      aria-selected={isSelected}
      onClick={() => onSelect(pipeline)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(pipeline);
        }
      }}
      tabIndex={0}
    >
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">
            {pipeline.pipelineName}
          </span>
          {/* Status chip */}
          {pipeline.isActive ? (
            <span className="bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full text-xs font-medium">
              active
            </span>
          ) : (
            <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-full text-xs font-medium">
              inactive
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Source type badge */}
          <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded text-xs text-white/60">
            {connectorLabel(pipeline.sourceType)}
          </span>
          <span className="text-white/30 text-xs" aria-hidden="true">
            →
          </span>
          {/* Target type badge */}
          <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded text-xs text-white/60">
            {connectorLabel(pipeline.targetType)}
          </span>
        </div>
      </div>

      {/* Hover-revealed actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          aria-label={`Edit pipeline ${pipeline.pipelineName}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(pipeline);
          }}
          className="p-1.5 text-white/40 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
        >
          <Pencil size={13} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Delete pipeline ${pipeline.pipelineName}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(pipeline);
          }}
          className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}
